<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants';

import MediaGrid from 'dashboard/components-next/media/MediaGrid.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const ITEMS_PER_PAGE = 15;

const store = useStore();
const { t } = useI18n();

const attachments = useMapGetter('mediaLibrary/getAttachments');
const meta = useMapGetter('mediaLibrary/getMeta');
const uiFlags = useMapGetter('mediaLibrary/getUIFlags');

const isFetching = computed(() => uiFlags.value.isFetching);
const currentPage = ref(1);

const filters = ref({
  contactId: '',
  fileType: '',
  since: '',
  until: '',
});

const fileTypeOptions = computed(() => [
  { value: '', label: t('MEDIA_LIBRARY.FILTERS.ALL_TYPES') },
  { value: ATTACHMENT_TYPES.IMAGE, label: t('MEDIA_LIBRARY.FILE_TYPES.IMAGE') },
  { value: ATTACHMENT_TYPES.AUDIO, label: t('MEDIA_LIBRARY.FILE_TYPES.AUDIO') },
  { value: ATTACHMENT_TYPES.VIDEO, label: t('MEDIA_LIBRARY.FILE_TYPES.VIDEO') },
  { value: ATTACHMENT_TYPES.FILE, label: t('MEDIA_LIBRARY.FILE_TYPES.FILE') },
]);

const hasContent = computed(() => attachments.value.length > 0);

const fetchAttachments = () => {
  store.dispatch('mediaLibrary/getAttachments', {
    contactId: filters.value.contactId || undefined,
    fileType: filters.value.fileType || undefined,
    since: filters.value.since || undefined,
    until: filters.value.until || undefined,
    page: currentPage.value,
  });
};

const applyFilters = () => {
  currentPage.value = 1;
  fetchAttachments();
};

const clearFilters = () => {
  filters.value = { contactId: '', fileType: '', since: '', until: '' };
  applyFilters();
};

const onPageChange = page => {
  currentPage.value = page;
  fetchAttachments();
};

onMounted(() => {
  fetchAttachments();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('MEDIA_LIBRARY.HEADER_TITLE') }}
      </h1>
    </header>

    <div class="flex flex-wrap items-end gap-3 px-6 py-4 border-b border-n-weak">
      <div class="w-48">
        <Input
          v-model="filters.contactId"
          :label="t('MEDIA_LIBRARY.FILTERS.CONTACT')"
          :placeholder="t('MEDIA_LIBRARY.FILTERS.CONTACT_PLACEHOLDER')"
          size="sm"
          type="number"
        />
      </div>
      <div class="w-40">
        <label class="mb-1 block text-sm font-medium text-n-slate-12">
          {{ t('MEDIA_LIBRARY.FILTERS.FILE_TYPE') }}
        </label>
        <ComboBox
          v-model="filters.fileType"
          :options="fileTypeOptions"
          :placeholder="t('MEDIA_LIBRARY.FILTERS.ALL_TYPES')"
        />
      </div>
      <div class="w-40">
        <Input
          v-model="filters.since"
          :label="t('MEDIA_LIBRARY.FILTERS.SINCE')"
          size="sm"
          type="date"
        />
      </div>
      <div class="w-40">
        <Input
          v-model="filters.until"
          :label="t('MEDIA_LIBRARY.FILTERS.UNTIL')"
          size="sm"
          type="date"
        />
      </div>
      <NextButton
        sm
        blue
        :label="t('MEDIA_LIBRARY.FILTERS.APPLY')"
        @click="applyFilters"
      />
      <NextButton
        sm
        slate
        faded
        :label="t('MEDIA_LIBRARY.FILTERS.CLEAR')"
        @click="clearFilters"
      />
    </div>

    <div class="flex-1 px-6 py-4 overflow-auto">
      <div v-if="isFetching" class="flex justify-center p-6">
        <Spinner />
      </div>
      <MediaGrid v-else-if="hasContent" :attachments="attachments" show-contact />
      <EmptyStateLayout
        v-else
        :title="t('MEDIA_LIBRARY.EMPTY_STATE.TITLE')"
        :subtitle="t('MEDIA_LIBRARY.EMPTY_STATE.SUBTITLE')"
      />
    </div>

    <PaginationFooter
      v-if="hasContent && meta.totalCount > ITEMS_PER_PAGE"
      :current-page="currentPage"
      :total-items="meta.totalCount"
      :items-per-page="ITEMS_PER_PAGE"
      @update:current-page="onPageChange"
    />
  </div>
</template>
